import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Skull, Crown, ChevronRight, Share2 } from "lucide-react";
import { api, absUrl } from "@/lib/api";
import { toast } from "sonner";

/**
 * /hunt/:slug — public landing for weekly-bounty share links.
 * Loads the enemy portrait + kill-count and shows a big CTA to sign in / play as guest.
 * Shared URL is UTM-tagged (?utm_source=hunt&utm_medium=share&utm_campaign=<slug>).
 * Falls back to the login page gracefully if the slug doesn't match a known enemy.
 */
export default function HuntPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [enemy, setEnemy] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!slug) return;
    (async () => {
      try {
        const res = await api.get("/bounty/featured?limit=20");
        const found = (res?.bounties || []).find((b) => b.slug === slug);
        if (found) setEnemy(found);
        else setNotFound(true);
      } catch {
        setNotFound(true);
      }
    })();
    // Set page title dynamically
    document.title = `Hunt · Champions of the Dawn Vanguard`;
  }, [slug]);

  const answerCall = async () => {
    if (busy) return;
    setBusy(true);
    try {
      // Play as guest so the hunt drop-off funnel actually converts
      await api.post("/auth/guest", {});
      navigate("/dashboard", { state: { hunt_slug: slug } });
    } catch {
      toast.error("Could not open the gate. Try again.");
      setBusy(false);
    }
  };

  const share = async () => {
    const url = `${window.location.origin}/hunt/${slug}?utm_source=hunt&utm_medium=share&utm_campaign=${encodeURIComponent(slug)}`;
    const text = enemy
      ? `⚔️ HUNT · ${enemy.name} — ${enemy.kills.toLocaleString()} souls claimed. Answer the call in Champions of the Dawn Vanguard.`
      : "Answer the call in Champions of the Dawn Vanguard — a tactical co-op descent.";
    if (navigator.share) {
      try { await navigator.share({ title: `Hunt: ${enemy?.name || "the ember"}`, text, url }); return; } catch { /* cancelled */ }
    }
    try {
      await navigator.clipboard.writeText(`${text}\n${url}`);
      toast.success("Hunt link copied.");
    } catch {
      window.open(`https://x.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, "_blank", "noopener,noreferrer");
    }
  };

  if (notFound || !slug) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" data-testid="hunt-not-found">
        <div className="panel p-10 max-w-lg text-center">
          <div className="text-[10px] tracking-[0.4em] ash-text">THE TRAIL HAS GONE COLD</div>
          <h1 className="font-heading text-4xl mt-3 text-[#f0e6d2] italic">Hunt Lost</h1>
          <p className="ash-text mt-3 text-sm italic">
            This foe has passed from the Ember's memory — or the trail never was.
          </p>
          <Link to="/" className="btn-glow inline-block mt-6" data-testid="hunt-back-btn">
            RETURN TO THE GATES
          </Link>
        </div>
      </div>
    );
  }

  if (!enemy) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="font-heading text-2xl ash-text">Reading the bounty parchment...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10" data-testid="hunt-page">
      <div
        className="panel p-6 md:p-10 max-w-2xl w-full relative overflow-hidden"
        style={{
          background: "radial-gradient(ellipse at top, rgba(90,20,10,0.25), rgba(10,5,3,0.98))",
          borderColor: "rgba(212,175,55,0.6)",
          boxShadow: "0 0 40px rgba(212,175,55,0.12), inset 0 0 60px rgba(0,0,0,0.6)",
        }}
      >
        {/* Weekly ribbon */}
        <div
          className="absolute top-0 right-0 pl-4 pr-3 py-1.5 flex items-center gap-1.5"
          style={{
            background: "linear-gradient(90deg, transparent, rgba(212,175,55,0.95))",
          }}
        >
          <Crown size={11} className="text-[#1a0f08]" />
          <span className="text-[9px] tracking-[0.35em] font-heading text-[#1a0f08]">
            BOUNTY · OF · THE · WEEK
          </span>
        </div>

        <div className="flex items-center gap-2 mb-4">
          <Skull size={14} className="text-[#c0392b]" />
          <span className="text-[10px] tracking-[0.45em] gold-text font-heading">WANTED · DEAD</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[220px,1fr] gap-6 items-start">
          <div
            className="relative rounded-sm overflow-hidden mx-auto md:mx-0"
            style={{
              width: 220,
              height: 293,
              boxShadow: "0 0 0 2px rgba(212,175,55,0.5), inset 0 0 30px rgba(0,0,0,0.8)",
            }}
          >
            <img
              src={absUrl(enemy.portrait)}
              alt={enemy.name}
              className="w-full h-full object-cover"
              style={{ filter: "sepia(0.3) contrast(1.05) saturate(0.9)" }}
              onError={(e) => { e.currentTarget.style.opacity = 0; }}
              data-testid="hunt-portrait"
            />
            {enemy.is_boss && (
              <div className="absolute top-2 right-2 text-[9px] px-2 py-1 bg-[#c0392b] text-[#f0e6d2] tracking-widest">
                BOSS
              </div>
            )}
          </div>

          <div>
            <h1
              className="font-heading text-4xl md:text-5xl italic text-[#f0e6d2] leading-tight"
              data-testid="hunt-name"
            >
              {enemy.name}
            </h1>

            <div className="mt-3 h-px bg-gradient-to-r from-[#d4af37]/60 via-[#d4af37]/20 to-transparent" />

            <div className="mt-4 flex items-baseline gap-3">
              <span className="font-heading text-5xl gold-text" data-testid="hunt-kills">
                {enemy.kills.toLocaleString()}
              </span>
              <span className="text-[11px] tracking-[0.3em] ash-text">
                {enemy.kills === 0 ? "UNCONTESTED" : enemy.kills === 1 ? "SOUL CLAIMED" : "SOULS CLAIMED"}
              </span>
            </div>

            <p className="mt-5 font-narrative text-[#f0e6d2] italic leading-relaxed text-[15px]">
              {enemy.kills === 0
                ? "First blood awaits. Be the first champion to strike this foe down — and let the Ember remember your name."
                : "Countless have fallen. The Ember still burns. Take up the blade and end this bounty — the deep is watching."}
            </p>

            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={answerCall}
                disabled={busy}
                className="btn-primary flex items-center gap-2 disabled:opacity-50"
                data-testid="hunt-answer-btn"
              >
                {busy ? "OPENING THE GATE..." : "ANSWER THE CALL"}
                {!busy && <ChevronRight size={14} />}
              </button>
              <button
                type="button"
                onClick={share}
                className="panel px-4 py-2 flex items-center gap-2 hover:border-[#d4af37] transition-colors"
                data-testid="hunt-share-btn"
              >
                <Share2 size={13} />
                <span className="text-[10px] tracking-[0.3em] ash-text">SHARE HUNT</span>
              </button>
            </div>
          </div>
        </div>

        <div className="mt-8 pt-4 border-t border-white/10 text-[10px] tracking-[0.3em] ash-text text-center">
          A ROGUE-LITE DESCENT · <Link to="/" className="gold-text hover:underline">DAWN VANGUARD</Link>
        </div>
      </div>
    </div>
  );
}
